import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Breadcrumb } from '@components/breadcrumb';
import { Card } from '@components/card';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState } from '@components/empty-state';
import { Header } from '@components/header';
import { IconShadowContainer } from '@components/icon-shadow-container';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconIntegration } from '@components/icons/integration';
import { IconIntegrations } from '@components/icons/integrations';
import { IntegrationIcon } from '@components/integrations/common';
import { ListCard } from '@components/list-card';
import { RelativeSpinner } from '@components/loading-spinner';
import { PageContent } from '@components/page-containers/page-container';
import { MiddleColumnPage } from '@components/page-containers/page-containers';
import { PillTabs } from '@components/pill-tabs';
import { SearchBox } from '@components/search-box';
import { Shapes } from '@components/shapes';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useMarketplaceTemplates } from 'api/app-marketplace/hooks';
import { ConnectionStatus } from 'api/app-marketplace/requests';
import { routePaths } from 'ui/authenticated/navigation';

type FilterValue = 'all' | 'connected' | 'available';

const StatusIndicator = ({ connectionStatus }: { connectionStatus: ConnectionStatus }) => {
  const { t } = useScopedTranslate(locale);

  if (connectionStatus === 'not_connected') return null;

  const isConnected = connectionStatus === 'connected';
  const color = isConnected ? 'elementGreen200' : 'elementRed100';

  return (
    <Stack orientation="horizontal" align="center" space={2}>
      <Shapes.Circle size={8} color={color} />
      <Text
        font="semibold12"
        color={color}
        text={isConnected ? t('connected') : t('disconnected')}
      />
    </Stack>
  );
};

export const AppMarketplace = () => {
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<FilterValue>('all');

  const templatesQuery = useMarketplaceTemplates({ search });
  const templates = useMemo(() => templatesQuery.data ?? [], [templatesQuery.data]);

  const { all, connected, available } = useMemo(() => {
    const connectedTemplates = templates.filter(
      (template) => template.connectionStatus !== 'not_connected'
    );
    const availableTemplates = templates.filter(
      (template) => template.connectionStatus === 'not_connected'
    );
    return {
      all: templates,
      connected: connectedTemplates,
      available: availableTemplates,
    };
  }, [templates]);

  const activeTemplates =
    status === 'connected' ? connected : status === 'available' ? available : all;

  const filters: { id: FilterValue; label: string; count: number }[] = [
    { id: 'all', label: t('filterAllLabel'), count: all.length },
    { id: 'connected', label: t('filterConnectedLabel'), count: connected.length },
    { id: 'available', label: t('filterAvailableLabel'), count: available.length },
  ];

  const selectedIndex = filters.findIndex((f) => f.id === status);

  return (
    <>
      <Header
        backAction={<IconIntegrations color="elementBlue100" />}
        breadcrumbs={<Breadcrumb items={[{ label: t('appMarketplace') }]} />}
        actions={<SearchBox onChange={setSearch} placeholder={t('searchPlaceholder')} />}
        filters={
          <PillTabs
            selectedIndex={selectedIndex >= 0 ? selectedIndex : 0}
            onChange={(index) => setStatus(filters[index]?.id ?? 'all')}
          >
            <PillTabs.Tabs>
              {filters.map((filter) => (
                <PillTabs.Tab key={filter.id} label={filter.label} count={filter.count} />
              ))}
            </PillTabs.Tabs>
          </PillTabs>
        }
      />
      <PageContent>
        <MiddleColumnPage>
          {templatesQuery.isLoading ? (
            <RelativeSpinner />
          ) : (
            <Stack space={6}>
              <InfoCard />
              {activeTemplates.length === 0 ? (
                search ? (
                  <EmptySearchState />
                ) : (
                  <EmptyState
                    icon={<IconIntegration size={80} />}
                    header={t('emptyTitle')}
                    content={t('emptyDescription')}
                  />
                )
              ) : (
                <Stack space={2}>
                  {activeTemplates.map((template) => (
                    <ListCard
                      key={template.shortId}
                      onClick={() =>
                        navigate(
                          routePaths.appMarketplaceDetail.replace(':templateId', template.shortId)
                        )
                      }
                      spaceX={6}
                      spaceY={5}
                      title={template.name}
                      description={template.description}
                      icon={<IntegrationIcon src={template.logo} />}
                      details={<StatusIndicator connectionStatus={template.connectionStatus} />}
                      iconRight={
                        template.connectionStatus === 'credentials_invalid' ? (
                          <IconFormIndicatorWarning color="elementOrange200" />
                        ) : undefined
                      }
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </MiddleColumnPage>
      </PageContent>
    </>
  );
};

const InfoCard = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Card space={6} hasBorder={false} variant="gray">
      <Stack orientation="horizontal" space={4} align="center">
        <IconShadowContainer
          icon={<IconIntegrations />}
          iconSize={40}
          shadowSize={120}
          containerSize={80}
        />
        <div>
          <Text font="bold16" text={t('cardTitle')} />
          <Text font="regular14" color="textSecondary" text={t('cardDescription')} />
        </div>
      </Stack>
    </Card>
  );
};

const locale = {
  en: {
    appMarketplace: 'App marketplace',
    searchPlaceholder: 'Search...',
    connected: 'Connected',
    disconnected: 'Disconnected',
    available: 'Available',
    emptyTitle: 'No apps yet',
    emptyDescription: 'Try adjusting your search or check back later.',
    cardTitle: 'Discover Apps & Integrations',
    cardDescription:
      'Connect your favorite tools to automate workflows and sync data effortlessly.',
    filterAllLabel: 'All',
    filterConnectedLabel: 'Connected',
    filterAvailableLabel: 'Available',
  },
};
